import { useState, type ReactElement } from 'react';
import { api, type User } from '../api';
import { useTitel } from '../titel';

export function Login({
  onSignedIn,
  gastLink = false,
}: {
  onSignedIn: (user: User) => void;
  /** Die Adresse war ein Projekt: vielleicht kam hier jemand mit einem Gast-Link. */
  gastLink?: boolean;
}): ReactElement {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [asked, setAsked] = useState(false);
  useTitel('Anmelden');

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { user } = await api.login(email.trim(), password);
      onSignedIn(user);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Anmeldung fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  /**
   * The answer is the same whether or not the address is one we know, so the
   * screen says the same thing too: anything else here is a way to find out
   * who has an account.
   */
  const forgot = async () => {
    if (!email.trim()) {
      setError('Bitte zuerst die E-Mail-Adresse eintragen.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.forgotPassword(email.trim());
    } catch {
      // Deliberately swallowed, for the same reason.
    } finally {
      setBusy(false);
      setAsked(true);
    }
  };

  return (
    <div className="page">
      <div className="marke-gross">
        <span className="appbar-mark" aria-hidden="true">S</span>
        <h1>
          SHA TV <span className="accent">Schnitt</span>
        </h1>
      </div>
      <p className="lede">Videoschnitt im Browser.</p>
      {gastLink ? (
        <p className="hint" data-gastlink-hinweis>
          Ohne eigenen Zugang? Dann war das der Link fürs Team. Zum Mitschneiden ohne Anmeldung gibt es einen eigenen
          Gast-Link -- bitte darum. Wer schon einen hat: ihn noch einmal aus der Nachricht öffnen.
        </p>
      ) : null}
      <form className="card" onSubmit={submit}>
        <label className="field">
          <span>E-Mail</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            inputMode="email"
            autoCapitalize="none"
            required
          />
        </label>
        <label className="field">
          <span>Passwort</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </label>
        <button className="primary" type="submit" disabled={busy} style={{ width: '100%' }}>
          {busy ? 'Einen Moment…' : 'Anmelden'}
        </button>
        {error ? <p className="error-text">{error}</p> : null}
      </form>
      {asked ? (
        <p className="hint" style={{ color: 'var(--ok-text)' }}>
          Wenn es zu dieser Adresse einen Zugang gibt, ist eine Mail mit einem Link unterwegs. Der Link gilt zwei
          Stunden.
        </p>
      ) : (
        <button className="ghost small" style={{ width: '100%' }} onClick={() => void forgot()} disabled={busy}>
          Passwort vergessen
        </button>
      )}
      <p className="hint">
        Keinen Zugang? Zugänge legt eine Administratorin an — Selbstregistrierung ist aus gutem Grund abgeschaltet.
      </p>
    </div>
  );
}
